import {
  compareVideoKindOrder,
  excerptLineageRank,
  isRailGroupLeadVideoKind,
} from "./videoKinds";

/** The fields rail lineage ordering reads. */
export type RailLineageRow = {
  id: string;
  type: string;
  sourceVideoId?: string | null;
  sourceVideoType?: string | null;
};

/**
 * The rails' LINEAGE presentation order, shared verbatim by the server (the
 * clips payload and the early-media lead pick) and the client (repair of
 * cached payloads) so the displayed first video and the warmed one can never
 * diverge:
 *
 * 1. Stable-sort by kind rank, then lineage rank (meeting-cut excerpts ahead
 *    of overview-sourced/unlinked ones) — within a bucket the caller's order
 *    survives (the server's canonical order, or a cached payload's).
 * 2. Interleave: each meeting cut present in the list is emitted immediately
 *    followed by its excerpts, so a strip renders "cut, its excerpts, next
 *    cut, its excerpts". The CUTS' relative order decides the group order —
 *    the same meetings order the Videos page and the funder Meetings strip
 *    show (the server sorts the cuts canonically before calling this; a
 *    cached payload keeps its stored cut order) — NOT the excerpts' own
 *    positions, which reflect per-excerpt drag/upload history and would let
 *    a later meeting's clips lead an earlier meeting's. Excerpts whose cut
 *    isn't in the list trail the cut groups, keeping the client's fallback
 *    "Meeting cut excerpts" panel contiguous.
 *
 * Idempotent: applying it to an already-ordered list returns the same order.
 */
export function orderRailLineage<T extends RailLineageRow>(rows: T[]): T[] {
  const sorted = [...rows].sort(
    (a, b) =>
      compareVideoKindOrder(a.type as never, b.type as never) ||
      excerptLineageRank(a.type, a.sourceVideoType ?? null) -
        excerptLineageRank(b.type, b.sourceVideoType ?? null),
  );
  const cutsById = new Map(
    sorted.filter((row) => row.type === "MEETING_CUT").map((r) => [r.id, r]),
  );
  const isMeetingExcerpt = (row: T) =>
    row.type === "EXCERPT" &&
    excerptLineageRank(row.type, row.sourceVideoType ?? null) === 1;

  // The head (overview kinds) and tail (other/unlinked excerpts) keep their
  // order; the middle regroups the meeting-cut excerpts by SOURCE, each
  // source's cut leading its excerpts, in the CUTS' order — a later
  // excerpt of an earlier source folds back to its group rather than
  // trailing another source's.
  const head = sorted.filter(
    (row) => row.type !== "MEETING_CUT" && !isMeetingExcerpt(row),
  );
  const tailStart = head.findIndex(
    (row) => excerptLineageRank(row.type, row.sourceVideoType ?? null) === 2,
  );
  const lead = tailStart === -1 ? head : head.slice(0, tailStart);
  const tail = tailStart === -1 ? [] : head.slice(tailStart);

  const bySource = new Map<string, T[]>();
  for (const row of sorted) {
    if (!isMeetingExcerpt(row)) continue;
    const key = row.sourceVideoId ?? "";
    const group = bySource.get(key);
    if (group) group.push(row);
    else bySource.set(key, [row]);
  }

  const middle: T[] = [];
  for (const [cutId, cut] of cutsById) {
    middle.push(cut);
    const group = bySource.get(cutId);
    if (group != null) middle.push(...group);
  }
  for (const [sourceId, group] of bySource) {
    if (!cutsById.has(sourceId)) middle.push(...group);
  }
  return [...lead, ...middle, ...tail];
}

/**
 * The video a rail's player opens with: the first row of a LINEAGE-ORDERED list
 * that the rail actually plays. Group lead cuts are skipped — they are
 * navigational thumbnails there (see `isRailGroupLeadVideoKind`), so leading
 * with one would both open a video whose transcript this payload deliberately
 * doesn't carry and bury the clips the rail exists to show.
 *
 * Shared, like the order itself, by the server's early-media lead pick, the
 * client's stored lead-clip hint, and the rail's own initial selection: the
 * warmed video and the selected one must never diverge, or the first play waits
 * on a media request that was spent on a different video.
 */
export function railLeadVideoRow<T extends { type?: string | null }>(
  rows: readonly T[],
): T | null {
  // A cached row with no kind predates lineage entirely — every row on these
  // rails was an excerpt then, so it counts as playable.
  return rows.find((row) => !isRailGroupLeadVideoKind(row.type ?? "")) ?? null;
}
